import { useState, type MouseEvent } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import Cookies from 'js-cookie'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { TinyTalkLogoFull } from '@/components/app/Logo'
import { loginRequestSchema, type LoginRequest } from '@/types/app/auth.type'
import { useAuth } from '@/db/queries/useAuth'
import { toast } from 'sonner'

const Login = () => {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { redirect?: string }
  const [showPassword, setShowPassword] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const { loginMutation } = useAuth()

  const authInputClass =
    'border-transparent bg-slate-100 shadow-none placeholder:text-slate-400 cursor-text select-text selection:bg-blue-500 selection:text-white focus-visible:border-transparent focus-visible:ring-2 focus-visible:ring-primary/30'

  const selectAllText = (event: MouseEvent<HTMLInputElement>) => {
    event.currentTarget.select()
  }

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginRequest>({
    resolver: zodResolver(loginRequestSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange', // Clear errors as user types after first submit
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const emailField = register('email', {
    onChange: () => setAuthError(null),
  })

  const passwordField = register('password', {
    onChange: () => setAuthError(null),
  })

  const onInvalidSubmit = () => {
    setAuthError('Please check the highlighted fields.')
    toast.error('Please check the highlighted fields.')
  }

  const onSubmit = async (data: LoginRequest) => {
    try {
      setAuthError(null)

      // Real API mode
      const response = await loginMutation.mutateAsync(data)

      // Store token
      if (response?.token) {
        const tokenKey = import.meta.env.VITE_TOKEN_NAME || 'token'
        localStorage.setItem(tokenKey, response.token)
        Cookies.set(tokenKey, response.token, { expires: 30, sameSite: 'lax' })

        const defaultDestination = '/dashboard'

        const requestedRedirect = search?.redirect
        const safeRedirect =
          requestedRedirect &&
            requestedRedirect.startsWith('/') &&
            !requestedRedirect.startsWith('/auth')
            ? requestedRedirect
            : defaultDestination

        const destination =
          response.user?.role !== 'ADMIN' && safeRedirect.startsWith('/dashboard/admin')
            ? '/dashboard'
            : safeRedirect

        toast.success('Login successful!')
        navigate({ to: destination })
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Invalid email or password.'
      setAuthError(errorMessage)
      toast.error(errorMessage)
    }
  }

  return (
    <div
      className="flex min-h-[100dvh] flex-col items-center justify-center overflow-x-hidden bg-gradient-to-b from-teal-50 via-white to-blue-50 px-3 py-6 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-[max(0.75rem,env(safe-area-inset-top))] sm:p-4 sm:py-8"
      style={{ scrollbarGutter: 'stable' }}
    >
      <div className="w-full max-w-md">
        {/* Logo — compact on small screens */}
        <div className="mb-4 flex justify-center sm:mb-8">
          <Link to="/" className="inline-block min-w-0 max-w-[min(100%,280px)] scale-[0.92] transition-transform sm:max-w-none sm:scale-100">
            <TinyTalkLogoFull />
          </Link>
        </div>

        <Card className="border-transparent shadow-lg sm:rounded-xl">
          <CardHeader className="space-y-1 px-4 pt-5 text-center sm:px-6 sm:pt-6">
            <CardTitle className="text-xl font-bold sm:text-2xl">Welcome Back</CardTitle>
            <CardDescription className="text-sm sm:text-base">
              Enter your credentials to access your account
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-5 sm:px-6 sm:pb-6">
            <form onSubmit={handleSubmit(onSubmit, onInvalidSubmit)} className="space-y-4">
              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter email address"
                  autoComplete="email"
                  autoFocus
                  {...emailField}
                  onClick={selectAllText}
                  className={`${authInputClass} ${errors.email ? 'border-red-500 focus-visible:ring-red-200' : ''}`}
                />
                {errors.email && (
                  <p className="text-sm text-red-500">{errors.email.message}</p>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                  <Label htmlFor="password">Password</Label>
                  <Link
                    to="/auth/forgot-password"
                    className="shrink-0 text-xs text-primary hover:text-primary/90 hover:underline sm:text-sm"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter password"
                    autoComplete="current-password"
                    {...passwordField}
                    onClick={selectAllText}
                    className={`${authInputClass} pr-10 ${errors.password ? 'border-red-500 focus-visible:ring-red-200' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-500">{errors.password.message}</p>
                )}
              </div>

              {authError && (
                <p className="text-sm text-red-600" role="alert">
                  {authError}
                </p>
              )}

              {/* Remember Me — shorter label on narrow screens */}
              <div className="flex items-start gap-2 sm:items-center sm:space-x-2">
                <Checkbox id="remember" className="mt-0.5 sm:mt-0" />
                <Label htmlFor="remember" className="cursor-pointer text-sm font-normal leading-snug">
                  Remember me
                  <span className="hidden sm:inline"> for 30 days</span>
                </Label>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                variant="default"
                className="w-full mt-2 font-semibold"
                disabled={isSubmitting || loginMutation.isPending}
              >
                {(isSubmitting || loginMutation.isPending) ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-5 sm:my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-transparent" />
              </div>
              <div className="relative flex justify-center text-xs sm:text-sm">
                <span className="bg-card px-2 text-slate-600">New to TinyTalk?</span>
              </div>
            </div>

            {/* Register Link */}
            <Link to="/auth/register">
              <Button variant="outline" className="w-full">
                Create an Account
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Footer — tighter typography on mobile */}
        <p className="mx-auto mt-4 max-w-[min(100%,340px)] text-center text-xs leading-relaxed text-muted-foreground sm:mt-6 sm:max-w-none sm:text-sm">
          By signing in, you agree to our{' '}
          <a href="#" className="text-primary hover:underline">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="#" className="text-primary hover:underline">
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  )
}

export default Login